import {
	calculateOasisFieldValuesOptions,
	getOasisAssessmentValuesOptions,
	startOfficeReviewOasisAddendumOptions,
	type OasisTaskViewModel
} from '@generated';
import { getOasisInitialValues } from '~/features/visits/oasis/values';
import { markOriginalCollaborationAnswers } from '~/features/visits/oasis/collaboration/util';
import { FormFields } from '~/libraries/forms/FormFields';
import { generateValidationSchema } from '~/libraries/forms/validation';
import { convertClientValuesToServerValues } from '~/libraries/forms/values';
import { Banner } from '~/shared/components/Banner';
import { Copyright } from '~/shared/Copyright';
import { FormModal } from '~/shared/forms/FormModal';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import type { FormValues } from '~/shared/forms/types';
import Yup from '~/util/yup';

export function OfficeReviewOasisAddendumFormModal({
	oasisTask,
	allOasisTasks,
	closeModal
}: {
	oasisTask: OasisTaskViewModel;
	allOasisTasks: OasisTaskViewModel[];
	closeModal: () => void;
}) {
	const { documentationFormControls, documentationFormFields, title, metadata } = oasisTask.oasisDefinition;
	const originalValues = oasisTask.values ?? {};

	const validationSchema = Yup.object().shape({
		...generateValidationSchema(documentationFormFields),
		divergenceReason: Yup.string().label('Addendum Reason').nullable().required('Please explain why the answers are being changed.')
	});

	const transformValuesForCalculation = (fieldName: string, values: FormValues) => ({
		oasisDefinitionId: oasisTask.oasisDefinitionId,
		fieldName,
		values: convertClientValuesToServerValues(values, documentationFormFields)
	});

	return (
		<FormModal
			title={`${title} · Office Review Addendum`}
			size="lg"
			initialValues={{ ...getOasisInitialValues(oasisTask, allOasisTasks, [], [], []), divergenceReason: null }}
			validationSchema={validationSchema}
			fieldCalculation={{ calculateMutation: calculateOasisFieldValuesOptions, transformValuesForCalculation }}
			saveMutation={{
				...startOfficeReviewOasisAddendumOptions,
				onSuccessMessage: 'Addendum sent to the clinician for response.',
				invalidateQueryKeys: [getOasisAssessmentValuesOptions(oasisTask.workflowCorrelationId).queryKey, ['certification-review']]
			}}
			onSaveTransform={values => ({
				oasisTaskId: oasisTask.id,
				values: convertClientValuesToServerValues(values, documentationFormFields),
				divergenceReason: values.divergenceReason
			})}
			submitButtonProps={{ submitLabel: 'Start Addendum' }}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			<div className="grid grid-cols-1 gap-8">
				<Banner
					intent="neutral"
					title="Office Review"
					message="The clinician's original answers are highlighted. Changing them here starts an addendum the clinician must accept or reject; their answers are not replaced until they accept."
				/>
				<FormFields
					formControls={documentationFormControls}
					formFields={documentationFormFields}
					additionalProcessing={formControls => markOriginalCollaborationAnswers(formControls, originalValues)}
				/>
				{metadata['copyright'] !== undefined && <Copyright copyright={metadata['copyright']} />}
				<TextAreaFormField name="divergenceReason" label="Addendum Reason" />
			</div>
		</FormModal>
	);
}
